import { isLoggedInAtom } from 'app/atoms/auth';
import { useScopedTranslation } from 'app/hooks/i18n';
import { usePageTitle } from 'app/utils/web-helpers';
import { useOnboardingNavigation } from 'app/screens/onboarding/credentials';
import { useContactInformationForm } from 'app/screens/onboarding/hooks/use-contact-information-form';
import { useContactInformationFlow } from 'app/screens/onboarding/hooks/use-contact-information-flow';
import { useNavigateToSignUpForm, useSignUpSheet } from 'app/screens/onboarding/signup';
import type { SpecificNavigationProp, SpecificRouteProp } from 'app/types/navigation';
import { useAtomValue } from 'jotai';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export function useContactInformationScreen({
  navigation,
  route,
}: {
  navigation: SpecificNavigationProp<'contact-information'>;
  route: SpecificRouteProp<'contact-information'>;
}) {
  const { t } = useScopedTranslation();
  const { t: tAPI } = useTranslation('api-errors');
  const { t: tOnboarding } = useTranslation('onboarding');
  const userType = route.params?.userType;
  const { progressActiveColor } = useOnboardingNavigation(userType);

  const tSuffix = userType === 'public' && t('prompt-public') !== 'prompt-public' ? '-public' : '';

  const { showSignUpSheet, signUpSheetState } = useSignUpSheet();
  const { control, formState, handleSubmit, reset } = useContactInformationForm();
  const navigateToSignUpForm = useNavigateToSignUpForm(true);
  const isLoggedIn = useAtomValue(isLoggedInAtom);

  const flow = useContactInformationFlow({
    handleSubmit,
    isLoggedIn,
    navigateToSignUpForm,
    resetForm: reset,
    showSignUpSheet,
  });

  const setPageTitle = usePageTitle('contact-information');
  useEffect(() => setPageTitle(`title${tSuffix}`), [setPageTitle, tSuffix]);

  const { step, onBackPress, ...flowRest } = flow;

  return {
    control,
    flowRest,
    formState,
    navigation,
    onBackPress,
    progressActiveColor,
    route,
    signUpSheetState,
    step,
    t,
    tAPI,
    tOnboarding,
    tSuffix,
  };
}
